'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  X,
  ChevronLeft,
  ChevronRight,
  Columns2,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { useImageTransform } from '@/shared/lib/hooks/useImageTransform';
import { ImageComparisonSlider } from './ImageComparisonSlider';

export interface ViewerImage {
  src: string;
  alt?: string;
  /** If provided, enables comparison mode for this image */
  beforeSrc?: string;
  /**
   * True when this item is the original angle photo (no render yet) — viewer renders it grayscale
   * to match the small angle card treatment, so users can clearly distinguish unrendered angles.
   */
  isOriginalAngle?: boolean;
}

interface ImageViewerProps {
  /** Single image source (backward compat) */
  src?: string;
  alt?: string;
  /** Multi-image array — takes precedence over src */
  images?: ViewerImage[];
  /** Initial image index for multi-image mode */
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

export function ImageViewer({
  src,
  alt = 'Image',
  images: imagesProp,
  initialIndex = 0,
  isOpen,
  onClose,
}: ImageViewerProps) {
  const t = useTranslations('imageViewer');

  // Normalize to array
  const images: ViewerImage[] =
    imagesProp && imagesProp.length > 0
      ? imagesProp
      : src
        ? [{ src, alt }]
        : [];

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const hasMultiple = images.length > 1;
  const currentImage = images[currentIndex] || images[0];
  const canCompare = !!currentImage?.beforeSrc;

  // Auto-enable comparison mode when beforeSrc is available
  useEffect(() => {
    setIsComparisonMode(canCompare);
  }, [canCompare]);

  const {
    position,
    scale,
    isDragging,
    containerRef,
    setPosition,
    setScale,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    zoomIn,
    zoomOut,
    resetTransform,
    positionRef,
    scaleRef,
  } = useImageTransform({
    minScale: 0.5,
    maxScale: 5,
    scaleStep: 1.25,
    enableZoom: true,
  });

  const goToPrevious = useCallback(() => {
    if (!hasMultiple) return;
    setCurrentIndex((i) => {
      const newIndex = i > 0 ? i - 1 : images.length - 1;
      setIsComparisonMode(!!images[newIndex]?.beforeSrc);
      return newIndex;
    });
    resetTransform();
  }, [hasMultiple, images, resetTransform]);

  const goToNext = useCallback(() => {
    if (!hasMultiple) return;
    setCurrentIndex((i) => {
      const newIndex = i < images.length - 1 ? i + 1 : 0;
      setIsComparisonMode(!!images[newIndex]?.beforeSrc);
      return newIndex;
    });
    resetTransform();
  }, [hasMultiple, images, resetTransform]);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      const openImage = images[initialIndex] || images[0];
      setIsComparisonMode(!!openImage?.beforeSrc);
      resetTransform();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          containerRef.current?.focus();
        });
      });
    }
  }, [isOpen, initialIndex]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          if (!isComparisonMode) zoomIn();
          break;
        case '-':
        case '_':
          if (!isComparisonMode) zoomOut();
          break;
        case '0':
          if (!isComparisonMode) resetTransform();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case 'c':
          if (canCompare) setIsComparisonMode((v) => !v);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    onClose,
    zoomIn,
    zoomOut,
    resetTransform,
    goToPrevious,
    goToNext,
    canCompare,
    isComparisonMode,
  ]);

  // Block page zoom and pointer events when viewer is open
  useEffect(() => {
    if (!isOpen) return;

    const preventGestureZoom = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
      }
    };

    const preventPageWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    const originalBodyStyle = document.body.style.overflow;
    const originalHtmlStyle = document.documentElement.style.overflow;
    const originalTouchAction = document.documentElement.style.touchAction;
    const originalBodyBackground = document.body.style.backgroundColor;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';
    document.body.style.backgroundColor = 'black';

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const originalThemeColor = themeColorMeta?.getAttribute('content') || '';
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', '#000000');
    }

    const viewportMeta = document.querySelector(
      'meta[name="viewport"]'
    ) as HTMLMetaElement | null;
    const originalViewportContent = viewportMeta?.getAttribute('content') || '';

    if (viewportMeta) {
      const newContent = originalViewportContent.replace(
        /user-scalable=yes|user-scalable=1/,
        'user-scalable=no'
      );
      if (!newContent.includes('user-scalable')) {
        viewportMeta.setAttribute('content', newContent + ', user-scalable=no');
      } else {
        viewportMeta.setAttribute('content', newContent);
      }
    }

    document.addEventListener('touchmove', preventGestureZoom, {
      passive: false,
    });
    document.addEventListener('wheel', preventPageWheel, { passive: false });

    return () => {
      document.body.style.overflow = originalBodyStyle;
      document.documentElement.style.overflow = originalHtmlStyle;
      document.documentElement.style.touchAction = originalTouchAction;
      document.body.style.backgroundColor = originalBodyBackground;

      if (themeColorMeta && originalThemeColor) {
        themeColorMeta.setAttribute('content', originalThemeColor);
      }

      if (viewportMeta && originalViewportContent) {
        viewportMeta.setAttribute('content', originalViewportContent);
      }

      document.removeEventListener('touchmove', preventGestureZoom);
      document.removeEventListener('wheel', preventPageWheel);
    };
  }, [isOpen]);

  // Add wheel event listener for image zoom with passive: false
  useEffect(() => {
    if (!isOpen || isComparisonMode) return;

    let cleanupListeners: (() => void) | undefined;

    const timeoutId = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      const wheelHandler = (e: WheelEvent) => {
        if (e.ctrlKey || e.metaKey) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const containerRect = container.getBoundingClientRect();
        const mouseX = e.clientX - containerRect.width / 2;
        const mouseY = e.clientY - containerRect.height / 2;

        const currentScale = scaleRef.current;
        const currentPosition = positionRef.current;
        const delta = e.deltaY > 0 ? 0.95 : 1.05;
        const newScale = Math.min(Math.max(currentScale * delta, 0.5), 5);

        const pointX = (mouseX - currentPosition.x) / currentScale;
        const pointY = (mouseY - currentPosition.y) / currentScale;

        const newX = mouseX - pointX * newScale;
        const newY = mouseY - pointY * newScale;

        setScale(newScale);
        setPosition({ x: newX, y: newY });
      };

      container.addEventListener('wheel', wheelHandler, { passive: false });
      window.addEventListener('wheel', wheelHandler, { passive: false });

      cleanupListeners = () => {
        container.removeEventListener('wheel', wheelHandler);
        window.removeEventListener('wheel', wheelHandler);
      };
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      cleanupListeners?.();
    };
  }, [
    isOpen,
    isComparisonMode,
    containerRef,
    scaleRef,
    positionRef,
    setScale,
    setPosition,
  ]);

  if (images.length === 0) return null;

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black"
          style={{
            width: '100vw',
            height: '100vh',
            minHeight: '-webkit-fill-available',
          }}
        />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 bg-black"
          style={{
            width: '100vw',
            height: '100vh',
            minHeight: '-webkit-fill-available',
            maxWidth: '100vw',
          }}
        >
          <DialogPrimitive.Title className="sr-only">
            {currentImage?.alt || alt || 'Image viewer'}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Use scroll to zoom, drag to pan, arrow keys to navigate, and ESC to
            close
          </DialogPrimitive.Description>
          <div
            ref={containerRef}
            className="relative w-full h-full flex items-center justify-center outline-none overflow-hidden bg-black"
            tabIndex={0}
            style={{
              pointerEvents: 'auto',
              touchAction: 'none',
            }}
            onClick={(e) => {
              if (e.target === containerRef.current) {
                onClose();
              }
            }}
            onWheel={(e) => {
              e.stopPropagation();
            }}
          >
            {/* Controls - bottom center on mobile, top-right on desktop */}
            <div className="fixed bottom-0 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-auto md:right-4 md:translate-x-0 z-10 flex items-center gap-1 bg-black/90 backdrop-blur-sm md:rounded-lg p-1 mb-1 md:mb-0">
              {!isComparisonMode && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={zoomOut}
                    title="Zoom out (-)"
                    className="h-9 w-9 text-white hover:bg-white/20"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-form-label min-w-[60px] text-center px-2 text-white">
                    {Math.round(scale * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={zoomIn}
                    title="Zoom in (+)"
                    className="h-9 w-9 text-white hover:bg-white/20"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={resetTransform}
                    title="Reset zoom (0)"
                    className="h-9 w-9 text-white hover:bg-white/20"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                </>
              )}
              {/* Comparison toggle */}
              {canCompare && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setIsComparisonMode((v) => !v);
                    resetTransform();
                  }}
                  title={t('compare') + ' (C)'}
                  className={`h-9 w-9 text-white hover:bg-white/20 ${isComparisonMode ? 'bg-white/20' : ''}`}
                >
                  <Columns2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                title="Close (ESC)"
                className="h-9 w-9 text-white hover:bg-white/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Page counter - top center */}
            {hasMultiple && (
              <div className="fixed top-4 left-1/2 -translate-x-1/2 z-10 text-white/80 text-list-item-subtitle bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                {currentIndex + 1} / {images.length}
              </div>
            )}

            {/* Previous button */}
            {hasMultiple && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="fixed left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 text-white/70 hover:text-white hover:bg-white/20 rounded-full"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            )}

            {/* Next button */}
            {hasMultiple && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="fixed right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 text-white/70 hover:text-white hover:bg-white/20 rounded-full"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            )}

            {/* Help text - desktop only */}
            <div className="hidden md:block fixed bottom-4 left-4 text-white/60 text-list-item-subtitle bg-black/50 backdrop-blur-sm px-3 py-1 rounded">
              {hasMultiple ? t('helpTextMulti') : t('helpText')}
            </div>

            {/* Content area */}
            {isComparisonMode && currentImage?.beforeSrc ? (
              /* Comparison slider mode */
              <div
                className="w-full h-full p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <ImageComparisonSlider
                  beforeImage={currentImage.beforeSrc}
                  afterImage={currentImage.src}
                  className="w-full h-full"
                  initialPosition={0}
                  objectFit="contain"
                />
              </div>
            ) : (
              /* Normal image mode */
              <div
                className="relative overflow-hidden cursor-move select-none"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                }}
              >
                <img
                  src={currentImage?.src}
                  alt={currentImage?.alt || alt}
                  className={`max-w-[90vw] max-h-[90vh] object-contain ${currentImage?.isOriginalAngle ? 'saturate-[0.25] brightness-95' : ''}`}
                  draggable={false}
                  fetchPriority="high"
                  loading="eager"
                />
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface ImageWithViewerProps {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  priority?: boolean;
}

export function ImageWithViewer({
  src,
  alt = 'Image',
  className = '',
  style,
  priority = false,
}: ImageWithViewerProps) {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  if (!src) {
    return null;
  }

  return (
    <>
      <img
        src={src}
        alt={alt}
        className={`cursor-pointer hover:opacity-90 transition-opacity ${className}`}
        style={style}
        onClick={() => setIsViewerOpen(true)}
        fetchPriority={priority ? 'high' : 'auto'}
        loading={priority ? 'eager' : 'lazy'}
      />
      <ImageViewer
        src={src}
        alt={alt}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
      />
    </>
  );
}
